/**
 * The "you ticked 12, here is what will actually happen" dialog.
 * For Media Devoted | Created By Ciro Autiero
 *
 * A bulk promote is the one action whose outcome is not the selection: caps are
 * per pool, so the dialog shows the arithmetic pool by pool and commits to a
 * number BEFORE anything is written. It never offers to make room — see
 * bulk-plan.ts for why evicting live elements to satisfy a multi-select is the
 * behaviour nobody would predict.
 */

import { FC } from 'react';
import type { QueueElement } from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui';

import type { BulkActionDef } from './bulk-actions';
import type { BulkPromotePlan } from './bulk-plan';

export interface BulkConfirmDialogProps {
  open: boolean;
  action: BulkActionDef | null;
  selected: QueueElement[];
  /** Present only for a promote. */
  plan: BulkPromotePlan | null;
  /** True when the action takes elements off the tracker. */
  writesTracker: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const PoolMath: FC<{ plan: BulkPromotePlan }> = ({ plan }) => (
  <ul className="mt-2 space-y-1 text-[12px]">
    {plan.pools.map((p) => (
      <li key={p.poolId} className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-muted-foreground">
          {p.poolName}
          {p.cap === null ? ' · no cap' : ` · ${p.active}/${p.cap} live`}
        </span>
        <span
          className={
            p.fits < p.selected
              ? 'shrink-0 tabular-nums text-warning'
              : 'shrink-0 tabular-nums text-muted-foreground'
          }
        >
          {p.fits} of {p.selected}
        </span>
      </li>
    ))}
  </ul>
);

const PromoteBody: FC<{ plan: BulkPromotePlan }> = ({ plan }) => (
  <>
    <p>
      {plan.fitting.length === 0
        ? 'None of the selected elements can go live — every pool is at capacity.'
        : `${plan.fitting.length} of ${plan.fitting.length + plan.overflow.length} selected can go live now.`}
    </p>
    <PoolMath plan={plan} />
    {plan.shortfall > 0 ? (
      <p className="mt-2 text-[12px] text-muted-foreground">
        The other {plan.shortfall} stay in the queue.{' '}
        <strong>Nothing live will be replaced.</strong> To fit more, raise the
        cap on the Rules tab, or use a single row&apos;s &ldquo;Move to live
        now&rdquo; to choose what it replaces.
      </p>
    ) : null}
  </>
);

const BulkConfirmDialog: FC<BulkConfirmDialogProps> = ({
  open,
  action,
  selected,
  plan,
  writesTracker,
  busy,
  onConfirm,
  onCancel,
}) => {
  if (!action) return null;
  const nothingToDo = plan !== null && plan.fitting.length === 0;
  const count = plan ? plan.fitting.length : selected.length;
  return (
    <ConfirmDialog
      open={open}
      title={`${action.label} — ${selected.length} selected`}
      description={
        plan ? (
          <PromoteBody plan={plan} />
        ) : (
          <>
            <p>
              {`${selected.length} element${selected.length === 1 ? '' : 's'} will be ${action.past}.`}
            </p>
            {/* Only a demote leaves the tracker, and only a demote can meet the
             *  floor — so the bank-only segments get no small print at all. The
             *  progress bar and the per-row results already say how the batch
             *  runs; the dialog does not need to narrate it. */}
            {writesTracker ? (
              <p className="mt-2 text-[12px] text-muted-foreground">
                They come off the tracker — the next rotation refills the slots.
                A pool never drops below its minimum live.
              </p>
            ) : null}
          </>
        )
      }
      confirmLabel={nothingToDo ? 'Close' : `${action.label} (${count})`}
      destructive={action.destructive}
      busy={busy}
      onConfirm={nothingToDo ? onCancel : onConfirm}
      onCancel={onCancel}
    />
  );
};

export default BulkConfirmDialog;
